<template>
  <el-dialog
    :title="title"
    width="100%"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :model-value="true"
    class="role-list"
  >
    <el-form
      ref="role-form"
      :model="roleObj"
      :rules="rules"
      :inline="true"
      class="d-flex"
    >
      <el-form-item
        label="Code"
        prop="name"
        class="width100 d-flex flex-grow-1"
      >
        <el-input v-model="roleObj.name" placeholder="Enter the code" :disabled="!hasCreatePermission" />
      </el-form-item>
      <el-form-item
        label="Description"
        prop="description"
        class="width100 d-flex flex-grow-2"
      >
        <el-input
          v-model="roleObj.description"
          placeholder="Enter the description"
          :disabled="!hasCreatePermission"
        />
      </el-form-item>
      <el-form-item label="Active" prop="is_active" class="flex-grow-1">
        <el-checkbox :disabled="roleObj.users_assigned || !hasCreatePermission" v-model="roleObj.is_active" />
      </el-form-item>
      <!-- Start of internal role check -->
      <el-form-item v-if="currentUser && currentUser.is_staff" label="Internal Role" prop="internal_role" class="flex-grow-1">
        <el-checkbox :disabled="!hasCreatePermission" v-model="roleObj.internal_role" />
      </el-form-item>
      <!-- End of internal role check -->
      <section>
        <el-tooltip effect="dark" content="Copy" placement="bottom">
          <el-button v-if="!isNew" :disabled="saving || !hasCreatePermission" @click="clone">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16 12.9v4.2c0 3.5-1.4 4.9-4.9 4.9H6.9C3.4 22 2 20.6 2 17.1v-4.2C2 9.4 3.4 8 6.9 8h4.2c3.5 0 4.9 1.4 4.9 4.9Z"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M22 6.9v4.2c0 3.5-1.4 4.9-4.9 4.9H16v-3.1C16 9.4 14.6 8 11.1 8H8V6.9C8 3.4 9.4 2 12.9 2h4.2C20.6 2 22 3.4 22 6.9Z"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </el-button>
        </el-tooltip>
        <el-tooltip effect="dark" content="Delete" placement="bottom">
          <el-button
            v-if="!isNew"
            :disabled="saving || roleObj.users_assigned || !hasCreatePermission"
            @click="deleteRole"
            icon="delete"
          />
        </el-tooltip>
        <el-button :loading="saving" type="primary" @click="save" :disabled="!hasCreatePermission">
          Submit
        </el-button>
        <el-button :disabled="saving" @click="cancel">Cancel</el-button>
      </section>
    </el-form>
    <section
      v-loading.body="loading"
      element-loading-text="Fetching permissions..."
      class="permission-container"
    >
      <section
        v-for="(permModuleObj, permModuleIdx) in permissionHash"
        :key="`mod-${permModuleIdx}`"
      >
        <el-checkbox
          v-model="permModuleObj.active"
          :disabled="!hasCreatePermission"
          @change="handleCheckModPerm(permModuleIdx, permModuleObj.active)"
          class="permission-group-name"
        >
          {{ permModuleObj.name }}
        </el-checkbox>
        <SubPermissionComp
          :subPermissions="permModuleObj.permission_json"
          :order="1"
          :parentPermObj="{ id: permModuleObj.id, index: permModuleIdx }"
          @handleSubPermission="handleModPermission"
        />
      </section>
    </section>
  </el-dialog>
</template>

<script>
import { http } from '@/plugins/http'
import { getHeaders, buildUrl } from '@/utils/api'
import { isEmpty, sortBy } from 'lodash'
import SubPermissionComp from './SubPermissionComp.vue'

export default {
  name: 'RoleForm',
  props: ['roleDetails', 'hasCreatePermission', 'currentUser'],
  components: { SubPermissionComp },
  data: () => ({
    roleObj: {
      name: null,
      description: null,
      isActive: true,
      internal_role: false,
      permissions: []
    },
    permissionHash: {},
    loading: false,
    rules: {
      name: {
        required: true,
        message: 'Please enter the code',
        trigger: 'blur'
      },
      description: {
        required: true,
        message: 'Please enter the description',
        trigger: 'blur'
      }
    }
  }),
  created () {
    this.loadRoleList()
    if (this.roleDetails) {
      this.roleObj = { ...this.roleDetails }
    }
  },
  computed: {
    isNew () {
      return !this.roleObj.id
    },
    title () {
      return !this.isNew ? 'Edit Role' : 'New Role'
    },
    currentRolePermHash () {
      return (this.roleObj.permissions || []).reduce((permHash, permKey) => {
        permHash[permKey] = true
        return permHash
      }, {})
    }
  },
  methods: {
    async loadRoleList () {
      try {
        this.loading = true

        const instanceUrl = this.$store.getters.getActiveInstance.url
        const headers = getHeaders(this.$store.getters.getToken)

        let { data } = await http.get(
          buildUrl('permission_modules', null, instanceUrl),
          { headers }
        )

        this.constructPermissionHash(data || [])
      } catch (e) {
      }
      this.loading = false
    },
    getPermissionList () {
      let activeIds = []

      const collectIds = permissionList => {
        for (const permission of permissionList) {
          if (permission.active) {
            activeIds.push(permission.id)
          }
          if (
            permission['sub-permissions'] &&
            permission['sub-permissions'].length > 0
          ) {
            collectIds(permission['sub-permissions'])
          }
        }
      }

      this.permissionHash.forEach(permMod => {
        collectIds(permMod.permission_json)
      })
      return activeIds
    },
    constructPermissionHash (permissionList) {
      let addActiveKey = permissionList => {
        let isActive = false

        for (let permissionObj of permissionList) {
          if (!isActive) {
            isActive = !!this.currentRolePermHash[permissionObj.id]
          }
          permissionObj.active = !!this.currentRolePermHash[permissionObj.id]

          if (
            permissionObj['sub-permissions'] &&
            permissionObj['sub-permissions'].length > 0
          ) {
            let {
              permissionList: subPermList,
              isActive: subIsActive
            } = addActiveKey(permissionObj['sub-permissions'])
            permissionObj['sub-permissions'] = subPermList

            if (!isActive && subIsActive) {
              isActive = subIsActive
            }
          }
        }
        return { isActive, permissionList }
      }
      this.permissionHash = sortBy(
        permissionList.map(permObj => {
          let { isActive, permissionList } = addActiveKey(
            permObj.permission_json
          )
          return {
            ...permObj,
            active: isActive,
            permission_json: permissionList
          }
        }),
        'order_number'
      )
    },
    enablePermission (permissionList, permVal) {
      for (let permission of permissionList) {
        permission.active = permVal
        if (
          permission['sub-permissions'] &&
          permission['sub-permissions'].length > 0
        ) {
          permission['sub-permissions'] = this.enablePermission(
            permission['sub-permissions'],
            permVal
          )
        }
      }
      return permissionList
    },
    handleModPermission (...modPermList) {
      const enableParentPerm = (permissionList, activePermList) => {
        if (!activePermList.length) return permissionList
        let [curPermObj, ...subPermList] = activePermList
        let { index, active } = curPermObj

        if (active !== undefined) permissionList[index].active = active

        if (
          permissionList[index]['sub-permissions'] &&
          permissionList[index]['sub-permissions'].length > 0
        ) {
          if (subPermList.length) {
            let {
              permissionList: subPermissionList,
              parentStatus: subPermParentStatus
            } = enableParentPerm(
              permissionList[index]['sub-permissions'],
              subPermList
            )
            permissionList[index]['sub-permissions'] = subPermissionList
            permissionList[index].active = subPermParentStatus
          } else if (!active) {
            permissionList[index]['sub-permissions'] = this.enablePermission(
              permissionList[index]['sub-permissions'],
              active
            )
          }
        }
        let parentStatus = permissionList.some(perm => perm.active)
        return { permissionList, parentStatus }
      }

      let [modPerm, ...permList] = modPermList
      let { index } = modPerm
      let { permissionList, parentStatus } = enableParentPerm(
        this.permissionHash[index].permission_json,
        permList
      )

      this.permissionHash[index].active = parentStatus
      this.permissionHash[index].permission_json = permissionList
    },
    handleCheckModPerm (permModIdx, permVal) {
      let curModPermList = this.enablePermission(
        this.permissionHash[permModIdx].permission_json,
        permVal
      )

      this.permissionHash[permModIdx].permission_json = curModPermList
    },
    async createRole () {
      const instanceUrl = this.$store.getters.getActiveInstance.url
      const headers = getHeaders(this.$store.getters.getToken)

      await http.post(buildUrl('roles', null, instanceUrl), this.roleObj, {
        headers
      })
      this.$notify({
        title: 'Saved',
        message: 'Role created successfully',
        type: 'success',
        customClass: 'successAlert'
      })
    },
    async updateRole () {
      const instanceUrl = this.$store.getters.getActiveInstance.url
      const headers = getHeaders(this.$store.getters.getToken)

      let { roleObj, roleDetails } = this
      let { id } = roleObj
      let roleKeys = ['name', 'description', 'is_active', 'internal_role', 'permissions']
      let payload = {}

      roleKeys.forEach(key => {
        if (key !== 'permissions') {
          if (roleDetails[key] !== roleObj[key]) {
            payload[key] = this.roleObj[key]
          }
        } else {
          if (roleDetails[key].length !== roleObj[key].length) {
            payload[key] = this.roleObj[key]
          } else {
            const initialGrpValue = roleDetails[key]
              .slice()
              .sort((a, b) => a - b)
            const updatedGrpValue = roleObj[key].slice().sort((a, b) => a - b)

            // Compare the sorted arrays
            let isEqual = initialGrpValue.every(
              (value, index) => value === updatedGrpValue[index]
            )

            if (!isEqual) payload[key] = this.roleObj[key]
          }
        }
      })

      if (isEmpty(payload)) this.cancel()

      payload.id = id

      await http.patch(
        buildUrl(`roles/${id}`, null, instanceUrl),
        payload,
        { headers }
      )
      this.$notify({
        title: 'Saved',
        message: 'Role updated successfully',
        type: 'success',
        customClass: 'successAlert'
      })
    },
    save () {
      this.$refs['role-form'].validate(async valid => {
        if (!valid) return

        this.saving = true
        this.roleObj.permissions = this.getPermissionList()
        if (!this.roleObj.permissions.length) {
          this.$notify({
            title: 'Error',
            message: 'Role should atleast have one permission',
            type: 'error'
          })

          this.saving = false
          return
        }

        try {
          if (this.isNew) await this.createRole()
          else await this.updateRole()
          this.$emit('onSave')
        } catch (e) {
        }
        this.saving = false
        this.cancel()
      })
    },
    cancel () {
      this.$emit('onClose')
    },
    clone () {
      this.roleObj = {
        name: null,
        description: null,
        isActive: true,
        internal_role: false,
        permissions: this.roleDetails.permissions
      }
    },
    async deleteRole () {
      try {
        let { name, id } = this.roleObj
        let value = await this.$confirm(
          `Are you sure you want to delete the role${name ? `: ${name}` : ''}`,
          'Warning',
          {
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            type: 'warning'
          }
        )
        if (!value) return

        const instanceUrl = this.$store.getters.getActiveInstance.url
        const headers = getHeaders(this.$store.getters.getToken)

        await http.delete(buildUrl(`roles/${id}`, null, instanceUrl), {
          headers
        })
        this.$notify({
          title: 'Deleted',
          message: 'Role deleted successfully',
          type: 'success',
          customClass: 'successAlert'
        })
        this.$emit('onDelete')
        this.cancel()
      } catch (e) {
      }
    },
    handleSubPermission (...val) {
      console.log('handleSubPermission', val)
    }
  }
}
</script>
<style scoped>
.d-flex {
  display: flex;
}
.flex-grow-1 {
  flex-grow: 1;
}
.flex-grow-2 {
  flex-grow: 2;
}
.permission-container {
  display: flex;
  flex-wrap: wrap;
  min-height: 200px;
  flex-direction: column;
  height: 1750px; /*Height should be increased as the permission list grows to maintain a consistent three-column layout.*/
  gap: 15px;

}
.permission-group-item-container {
  display: flex;
  flex-direction: column;
}
.permission-group-name {
  font-weight: 700;
  text-transform: uppercase;
  width: 100%;
  background-color: #f0f0f0;
  align-items: center;
  display: inline-flex;
  height: 32px;
}
.el-checkbox + .el-checkbox {
  margin-left: 0px;
}
svg {
  width: 14px;
  height: 14px;
}
</style>
<style>
.el-form-item.width100 .el-form-item__content {
  width: 100%;
}
.role-list.el-dialog__wrapper .el-dialog.el-dialog--full {
  width: 95%;
  height: 95%;
  top: 2.5%;
}
</style>
